/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { accessAPI, TablaFiltrable, Titulo, useIsMobile, useOrganismo, tituloBase } from "../../shared";
import RequiereAtencion from "../inicio/RequiereAtencion";
import {
  diasDesde,
  ESTADOS_HALLAZGO,
  fechaCorta,
  severidadTipoHallazgo,
  textoEstadoHallazgo
} from "../../portal/utilidades";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import SkeletonListado from "../SkeletonListado";

const CAMPOS_BUSQUEDA = [
  "resumen",
  "texto",
  "hallazgoTipo.nombre",
  "actividad.proceso.nombre",
  "actividad.proceso.nombrefantasia"
];

export default function Hallazgos() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Hallazgos";
    return () => (document.title = tituloBase());
  }, []);

  const organismo = useOrganismo();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [hallazgos, setHallazgos] = useState(null);

  useEffect(() => {
    accessAPI("GET", "cliente/hallazgo", null, setHallazgos, () => setHallazgos([]));
  }, []);

  // Campos planos para los desplegables de la tabla
  const filas = useMemo(
    () =>
      (hallazgos || []).map((h) => ({
        ...h,
        _tipo: h.hallazgoTipo?.nombre || "Hallazgo",
        _estado: textoEstadoHallazgo(h.estado, organismo)
      })),
    [hallazgos, organismo]
  );

  const tipo = (h) => (
    <Tag severity={severidadTipoHallazgo(h.hallazgoTipo)}>{h.hallazgoTipo?.nombre || "Hallazgo"}</Tag>
  );
  // En mobile, solo el ícono (el texto queda como title); un estado sin
  // ícono muestra el texto igual
  const estado = (h) => {
    const { severidad, icono } = ESTADOS_HALLAZGO[h.estado] || {};
    const texto = textoEstadoHallazgo(h.estado, organismo);
    return isMobile && icono ? (
      <Tag severity={severidad} icon={icono} title={texto} />
    ) : (
      <Tag severity={severidad} icon={icono}>
        {texto}
      </Tag>
    );
  };
  // Fecha de creación y, debajo, cuánto lleva abierto o cuándo se cerró
  // (igual que la columna Vencimiento de Certificados)
  const creado = (h) => {
    const dias = diasDesde(h.fechaCreacion);
    return (
      <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "flex-start" }}>
        <span>{fechaCorta(h.fechaCreacion)}</span>
        {h.estado === "CERRADO" ? (
          <Tag severity="secondary">Cerrado el {fechaCorta(h.fechaCierre)}</Tag>
        ) : (
          <Tag
            // Días abierto: verde hasta 3, ámbar hasta 5, rojo después
            severity={dias <= 3 ? "success" : dias <= 5 ? "warning" : "danger"}
            icon="pi pi-clock"
          >
            {dias} {dias === 1 ? "día" : "días"} abierto
          </Tag>
        )}
      </div>
    );
  };

  // Hasta tener los datos, el esqueleto de la sección (como en el admin)
  if (!hallazgos) return <SkeletonListado filtros={2} />;

  return (
    <>
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        etiqueta="Nuevo"
        neutro
        subtitulo="No conformidades y observaciones de tus procesos, y su seguimiento."
      >
        Hallazgos
      </Titulo>

      {/* Grilla con 1em entre cards, también cuando aparece la de pendientes */}
      <div css={{ display: "grid", gap: "1em", marginBottom: "1em" }}>
        <RequiereAtencion soloHallazgos urlVolver="/hallazgos" />

        <Card>
          {/* Sin hallazgos, el cartel en vez de la tabla vacía */}
          {hallazgos?.length === 0 ? (
            <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
              <i className="pi pi-check-circle" css={{ fontSize: "1.5rem" }} />
              <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>No hay hallazgos</div>
            </div>
          ) : (
            <TablaFiltrable
              value={filas}
              searchFields={CAMPOS_BUSQUEDA}
              filtros={[
                { campo: "_estado", label: "Estado", labelPlural: "estados", siempreVisible: true },
                { campo: "_tipo", label: "Tipo", labelPlural: "tipos", siempreVisible: true }
              ]}
              nombreArchivo="hallazgos"
              emptyMessage="No hay hallazgos"
              className="p-tabla filas-clickeables"
              onRowClick={(e) => navigate(`/hallazgo/${e.data.id}`)}
            >
              <Column header="Tipo" body={tipo} exportValue={(h) => h._tipo} />
              {!isMobile && (
                <Column
                  header="Hallazgo"
                  body={(h) => (
                    <>
                      <div className="p-celda-titulo">{h.resumen}</div>
                      <div className="p-celda-sub">{h.actividad?.tipoNombre || h.actividad?.actividadtipo?.nombre}</div>
                    </>
                  )}
                  exportValue={(h) => h.resumen}
                />
              )}
              <Column
                header="Proceso"
                body={(h) => (
                  <>
                    <div className="p-celda-titulo">{h.actividad?.proceso?.nombre}</div>
                    <div className="p-celda-sub">{h.actividad?.proceso?.nombrefantasia}</div>
                  </>
                )}
                exportValue={(h) =>
                  [h.actividad?.proceso?.nombre, h.actividad?.proceso?.nombrefantasia].filter(Boolean).join(" - ")
                }
              />
              {!isMobile && <Column header="Creado" body={creado} exportValue={(h) => fechaCorta(h.fechaCreacion)} />}
              <Column header="Estado" body={estado} exportValue={(h) => h._estado} />
            </TablaFiltrable>
          )}
        </Card>
      </div>
    </>
  );
}
